import { PaymentMethodType } from '@/enums';
import { TransactionFeesType } from '@/types';

export type TransactionFeeMap = Record<
  TransactionFeesType,
  Partial<Record<PaymentMethodType, number>>
>;

interface TransactionFeeSource {
  flat_transaction_fees?: Partial<
    Record<PaymentMethodType, number | null>
  > | null;
  transaction_fees?: Partial<Record<PaymentMethodType, number | null>> | null;
}

const isFeeSet = (fee: number | null | undefined): fee is number =>
  typeof fee === 'number' && fee >= 0;

// Chat-level fees store an explicit null when the customer is on the supplier
// default, so they only override when set. Within a level, a percentage fee
// takes precedence over a flat one, and debit card falls back to credit card.
export const buildTransactionFeeMap = (
  supplierMetadata: TransactionFeeSource | null | undefined,
  supplierChat: TransactionFeeSource | null | undefined,
): TransactionFeeMap => {
  const feeMap: TransactionFeeMap = { FLAT: {}, PERCENTAGE: {} };

  Object.values(PaymentMethodType).forEach((paymentMethodType) => {
    for (const feeSource of [supplierChat, supplierMetadata]) {
      let percentageFee = feeSource?.transaction_fees?.[paymentMethodType];
      let flatFee = feeSource?.flat_transaction_fees?.[paymentMethodType];

      // Not all customers have debit card fees, so we use the credit card
      // fees if debit card fees are not set at this level
      if (
        paymentMethodType === PaymentMethodType.DEBIT_CARD &&
        !isFeeSet(percentageFee) &&
        !isFeeSet(flatFee)
      ) {
        percentageFee =
          feeSource?.transaction_fees?.[PaymentMethodType.CREDIT_CARD];
        flatFee =
          feeSource?.flat_transaction_fees?.[PaymentMethodType.CREDIT_CARD];
      }

      if (isFeeSet(percentageFee)) {
        feeMap.PERCENTAGE[paymentMethodType] = percentageFee;
        return;
      }

      if (isFeeSet(flatFee)) {
        feeMap.FLAT[paymentMethodType] = flatFee;
        return;
      }
    }
  });

  // Set the plaid ach fee to the ach fee
  if (feeMap.FLAT[PaymentMethodType.ACH]) {
    feeMap.FLAT[PaymentMethodType.PLAID_ACH] =
      feeMap.FLAT[PaymentMethodType.ACH];
  }

  if (feeMap.PERCENTAGE[PaymentMethodType.ACH]) {
    feeMap.PERCENTAGE[PaymentMethodType.PLAID_ACH] =
      feeMap.PERCENTAGE[PaymentMethodType.ACH];
  }

  return feeMap;
};
